import React, { useEffect, useState } from 'react';
import { useWeb3 } from '../context/Web3Context';
import Card from '../components/Common/Card';
import PageLoader from '../components/Loader/PageLoader';
import AdminStats from '../components/Admin/AdminStats';
import FeeManager from '../components/Admin/FeeManager';
import PlanManager from '../components/Admin/PlanManager';
import VirtualInvestor from '../components/Admin/VirtualInvestor';
import UserManager from '../components/Admin/UserManager';
import RouterPool from '../components/Admin/RouterPool';
import OwnerControls from '../components/Admin/OwnerControls';
import ProtocolTiming from '../components/Admin/ProtocolTiming';
import UserList from '../components/Admin/UserList';

const AdminPanel = () => {
  const { contract, account, isOwner, isAdmin, isPrivileged, ownerAddr, adminAddr } = useWeb3();

  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);

  const [stats, setStats] = useState({});
  const [fees, setFees] = useState({});
  const [limits, setLimits] = useState({});
  const [plans, setPlans] = useState({});
  const [dailyLimit, setDailyLimit] = useState('0');
  const [routerPool, setRouterPool] = useState('0');
  const [adminWallet, setAdminWallet] = useState(null);
  const [roiTimeframe, setRoiTimeframe] = useState('0');
  const [lookupTarget, setLookupTarget] = useState({ value: '', tick: 0 });

  const handleSelectUser = (id) => {
    setLookupTarget({ value: id, tick: Date.now() });
  };

  const load = async () => {
    if (!contract) { setLoading(false); return; }
    try {
      const calls = [
        contract.getContractStats(),
        contract.getFees(),
        contract.getInvestmentLimits(),
        contract.getAllPlans(),
        contract.dailyWithdrawLimit(),
        contract.adminWallet(),
        contract.roiTimeframe()
      ];
      if (isOwner) {
        calls.push(contract.routerPool());
      }
      const results = await Promise.all(calls);
      const [s, f, lim, pl, dl, aw, rt, rp] = results;

      setStats({
        totalUsers: s._totalUsers || s[0],
        totalInvestmentAmount: (s.totalInvestmentAmount || s[1])?.toString(),
        activeCount: s.activeCount || s[2],
        blacklistedCount: s.blacklistedCount || s[3],
        contractBalance: (s.contractBalance || s[4])?.toString()
      });
      setFees({
        adminWallet: (f._adminWalletFee || f[0])?.toString(),
        routerPool: (f._routerPoolFee || f[1])?.toString(),
        claim: (f._claimFee || f[2])?.toString(),
        early: (f._earlyWithdrawFee || f[3])?.toString()
      });
      setLimits({
        min: lim[0].toString(),
        multiple: lim[1].toString()
      });
      setPlans({
        dailyROIs: pl.dailyROIs || pl[0],
        periods: pl.periods || pl[1],
        maxROIBps: pl.maxROIBps || pl[2]
      });
      setDailyLimit(dl.toString());
      setAdminWallet(aw);
      setRoiTimeframe(rt.toString());
      if (rp !== undefined) setRouterPool(rp.toString());
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [contract, account, isOwner, reloadKey]);

  const refresh = () => setReloadKey((k) => k + 1);

  if (!account) {
    return (
      <Card>
        <div className="empty-state">
          <div className="empty-state-icon">◌</div>
          <div className="empty-state-text">Connect your wallet</div>
        </div>
      </Card>
    );
  }

  if (loading) return <PageLoader text="Loading Admin" />;

  if (!isPrivileged) {
    return (
      <Card>
        <div className="empty-state">
          <div className="empty-state-icon">⊘</div>
          <div className="empty-state-text">Access denied</div>
          <div style={{
            marginTop: 12,
            fontSize: 13,
            color: 'var(--text-muted)'
          }}>
            This area is restricted to authorized accounts.
          </div>
        </div>
      </Card>
    );
  }

  return (
    <div>
      <h1 className="page-title">Control Panel</h1>
      <p className="page-subtitle">
        Connected as {isOwner ? 'Owner' : 'Admin'} · Protocol-level configuration
      </p>

      <AdminStats stats={stats} />

      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        <UserList onSelect={handleSelectUser} reloadKey={reloadKey} />

        <UserManager prefillTarget={lookupTarget} />

        <FeeManager
          fees={fees}
          limits={limits}
          dailyLimit={dailyLimit}
          onChange={refresh}
        />

        <VirtualInvestor onChange={refresh} />

        {/* Owner-only: plan configuration changes are restricted to onlyOwner */}
        {isOwner && <PlanManager plans={plans} onChange={refresh} />}

        {/* Owner-only: ROI timeframe (one-period duration) — affects all plans */}
        {isOwner && (
          <ProtocolTiming
            currentSeconds={roiTimeframe}
            onChange={refresh}
          />
        )}

        {/* Owner-only: router pool details are hidden from admins entirely */}
        {isOwner && (
          <RouterPool
            routerPool={routerPool}
            routerPoolFee={fees.routerPool}
            onChange={refresh}
          />
        )}

        {/* Owner-only: ownership transfer / emergency withdraw */}
        {isOwner && (
          <OwnerControls
            ownerAddr={ownerAddr}
            adminAddr={adminAddr}
            adminWallet={adminWallet}
            onChange={refresh}
          />
        )}
      </div>
    </div>
  );
};

export default AdminPanel;
